import { billingModelSummaryFrom } from '@/components/organisms/jobs/recurring/recurringDetail.helpers';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import type { ContactPaymentMethod } from '@/types/contact';
import type { InvoicingSchedule } from '@/types/job';
import type {
  RecurringBillingFailureCode,
  RecurringBillingPlanStatus,
  RecurringBillingPlanSummary,
  RecurringBillingRepairLinkResponse,
  RecurringBillingRunStatus,
  RecurringBillingRunSummary,
} from '@/types/recurring-billing';

export type RecurringBillingTab = 'plans' | 'runs' | 'exceptions';
export type SelectedRecord =
  | { type: 'plan'; item: RecurringBillingPlanSummary }
  | { type: 'run'; item: RecurringBillingRunSummary }
  | null;
export type PlanColumnKey = 'job' | 'contact' | 'billing' | 'status' | 'nextRun' | 'lastResult';
export type RunColumnKey = 'scheduled' | 'job' | 'contact' | 'status' | 'outcome';

export function normalizeOptionalSearchId(value: unknown) {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed && trimmed !== 'null' ? trimmed : undefined;
}

export function buildRecurringBillingSearch({
  tab,
  jobId,
  contactId,
  planId,
}: {
  tab: RecurringBillingTab;
  jobId?: string;
  contactId?: string;
  planId?: string;
}) {
  return {
    tab,
    jobId: jobId || undefined,
    contactId: contactId || undefined,
    planId: tab === 'runs' ? planId || undefined : undefined,
  };
}

export function normalizeTab(value: unknown): RecurringBillingTab {
  return value === 'runs' || value === 'exceptions' ? value : 'plans';
}

export function formatDateTime(value: string | null | undefined, timeZone?: string | null) {
  if (!value) return '-';
  return (
    formatDateTimeInTimeZone(value, timeZone, { dateStyle: 'medium', timeStyle: 'short' }) || '-'
  );
}

export function formatRelativeDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  const diff = date.getTime() - Date.now();
  const absMinutes = Math.round(Math.abs(diff) / 60000);
  if (absMinutes < 1) return 'now';
  if (absMinutes < 60) return `${absMinutes}m ${diff < 0 ? 'ago' : 'from now'}`;
  const absHours = Math.round(absMinutes / 60);
  if (absHours < 48) return `${absHours}h ${diff < 0 ? 'ago' : 'from now'}`;
  const absDays = Math.round(absHours / 24);
  return `${absDays}d ${diff < 0 ? 'ago' : 'from now'}`;
}

export function titleCase(value: string | null | undefined) {
  if (!value) return '-';
  return value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function failureTitle(code: RecurringBillingFailureCode | null | undefined) {
  switch (code) {
    case 'GATEWAY_UNAVAILABLE':
      return 'Payment gateway temporarily unavailable';
    case 'GATEWAY_CREDENTIALS':
      return 'Payment gateway credentials invalid';
    case 'GATEWAY_DECLINED':
      return 'Saved method declined';
    case 'GATEWAY_ACTION_REQUIRED':
      return 'Customer action required';
    case 'MISSING_PAYMENT_METHOD':
      return 'Saved method missing';
    case 'MISSING_CONTACT':
      return 'Contact missing';
    case 'INVOICE_NOT_PAYABLE':
      return 'Invoice not payable';
    case 'RUN_FAILED':
      return 'Recurring run failed';
    default:
      return titleCase(code);
  }
}

export function failureGuidance(code: RecurringBillingFailureCode | null | undefined) {
  switch (code) {
    case 'GATEWAY_UNAVAILABLE':
      return 'Retry once the payment gateway recovers.';
    case 'GATEWAY_CREDENTIALS':
      return 'Repair gateway credentials before retrying.';
    case 'GATEWAY_DECLINED':
      return 'Replace the saved method or send a repair link.';
    case 'GATEWAY_ACTION_REQUIRED':
      return 'Send a repair link so the customer can update the method.';
    case 'MISSING_PAYMENT_METHOD':
      return 'Attach a saved payment method before running billing again.';
    case 'MISSING_CONTACT':
      return 'Attach a contact to the recurring job.';
    case 'INVOICE_NOT_PAYABLE':
      return 'Review the generated invoice before retrying.';
    case 'RUN_FAILED':
      return 'Review the latest error before retrying.';
    default:
      return 'Review the latest failure details.';
  }
}

/**
 * Mirrors the server's gate on `POST plans/:id/retry-last-failure`: a retry
 * from the office can clear a transient outage or an operator/config fault the
 * office has just fixed (credentials, a disabled processor, a missing contact
 * or saved method, an invoice that was not payable, an unclassified crash). A
 * customer-payment failure — a decline, or a 3DS challenge — is not retried
 * blind; it clears through the repair link or a new saved method, so the server
 * rejects it and the action must not be offered.
 */
const CUSTOMER_PAYMENT_FAILURE_CODES: readonly RecurringBillingFailureCode[] = [
  'GATEWAY_DECLINED',
  'GATEWAY_ACTION_REQUIRED',
];

export function isRetryableFailureCode(code: RecurringBillingFailureCode | null | undefined) {
  return !!code && !CUSTOMER_PAYMENT_FAILURE_CODES.includes(code);
}

export function planStatusBadge(status: RecurringBillingPlanStatus) {
  switch (status) {
    case 'ACTIVE':
      return 'bg-emerald-100 text-emerald-800';
    case 'PAUSED':
      return 'bg-amber-100 text-amber-900';
    case 'FAILED':
      return 'bg-rose-100 text-rose-900';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export function runStatusBadge(status: RecurringBillingRunStatus) {
  switch (status) {
    case 'CHARGED':
      return 'bg-emerald-100 text-emerald-800';
    case 'INVOICED':
      return 'bg-blue-100 text-blue-900';
    case 'PROCESSING':
    case 'DEFERRED':
      return 'bg-amber-100 text-amber-900';
    case 'FAILED':
      return 'bg-rose-100 text-rose-900';
    case 'SKIPPED':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-slate-100 text-slate-800';
  }
}

/**
 * Why a run failed, or null when it did not. Only a failed run has a failure:
 * a run that failed and then settled on a retry kept its old error, and was
 * shown as "Recurring run failed" next to "Invoiced".
 */
export function runFailure(
  run: Pick<RecurringBillingRunSummary, 'status' | 'errorCode' | 'errorMessage'>,
): { title: string; message: string | null } | null {
  if (run.status !== 'FAILED') return null;
  return { title: failureTitle(run.errorCode), message: run.errorMessage };
}

/**
 * What a run came to when it has no error. Only an invoiced or charged run
 * raised an invoice; "Invoice created" was said of every run without an error.
 */
export function runOutcomeLabel(
  run: Pick<RecurringBillingRunSummary, 'status' | 'paymentId'> & {
    plan?: Pick<RecurringBillingRunSummary['plan'], 'status'>;
  },
): string {
  switch (run.status) {
    case 'CHARGED':
      // Settled with no charge: a deposit, the customer's credit or a payment
      // taken elsewhere had already paid the invoice.
      return run.paymentId ? 'Invoice created and charged' : 'Invoice already paid';
    case 'INVOICED':
      return run.paymentId ? 'Invoice created and charged' : 'Invoice created';
    case 'SKIPPED':
      return 'Nothing to bill';
    case 'DEFERRED':
      // An active series bills its held visits on the next dispatch; a
      // closed plan never resumes, so the office bills the visit from its job.
      return run.plan?.status === 'ACTIVE'
        ? 'Bills within 15 minutes'
        : run.plan?.status === 'CANCELLED'
          ? 'Not billed · use Bill visit on the job'
          : 'Bills when billing resumes';
    default:
      return 'In progress';
  }
}

/**
 * Why a completed visit is held for billing. A series moved off a month-end or
 * as-needed schedule holds the visits it still owed while it is active, so a
 * held visit no longer means billing was paused.
 */
export function deferredRunDetail(run: {
  plan?: Pick<RecurringBillingRunSummary['plan'], 'status'>;
}): string {
  return run.plan?.status === 'ACTIVE'
    ? 'Completed visit waiting for its invoice'
    : 'Visit completed while billing was paused';
}

/**
 * What asking for a repair link did. Only an emailed link reached the
 * customer: one with no address to send to, or whose email failed, still has
 * to be handed over, so the office is told to share it.
 */
export function repairLinkOutcome(
  response: Pick<RecurringBillingRepairLinkResponse, 'deliveryStatus' | 'sentTo' | 'deliveryError'>,
): { emailed: boolean; message: string } {
  if (response.deliveryStatus === 'SENT') {
    return {
      emailed: true,
      message: response.sentTo
        ? `Repair link emailed to ${response.sentTo}`
        : 'Repair link emailed to the customer',
    };
  }
  if (response.deliveryStatus === 'FAILED') {
    return {
      emailed: false,
      message: 'Repair link created, but the email failed. Share the link with the customer.',
    };
  }
  // Not sent: the customer opted out of email, or has no address on file.
  return {
    emailed: false,
    message: response.deliveryError?.startsWith('suppressed')
      ? 'Repair link created, not emailed: the customer opted out of email. Share the link with them.'
      : 'Repair link created, not emailed: the customer has no email address. Share the link with them.',
  };
}

export function planLabel(plan: RecurringBillingPlanSummary) {
  return plan.job.jobNumber ? `${plan.job.jobNumber} - ${plan.job.name}` : plan.job.name;
}

export function contactLabel(record: Pick<RecurringBillingPlanSummary, 'contact'>) {
  return (
    record.contact?.displayName ??
    record.contact?.companyName ??
    record.contact?.primaryEmail ??
    'No contact'
  );
}

export function methodLabel(method: ContactPaymentMethod | undefined) {
  if (!method) return 'Unknown saved method';
  if (method.brand || method.last4) {
    return `${(method.brand ?? method.type ?? 'Card').toUpperCase()} **** ${method.last4 ?? '0000'}`;
  }
  return method.label ?? method.providerPaymentMethodId ?? method.id;
}

export function storedScheduleLabel(plan: RecurringBillingPlanSummary) {
  const recurringConfig = (plan.scheduleConfig?.recurringConfig ?? null) as {
    frequency?: string;
    startDate?: string;
    endType?: string;
  } | null;

  return titleCase(recurringConfig?.frequency ?? plan.invoicingSchedule);
}

export function billingModeLabel(plan: RecurringBillingPlanSummary) {
  return plan.autoCharge ? 'Auto-charge' : 'Invoice only';
}

/**
 * How the series bills, from the copy of the job's recurring config the plan
 * keeps (absent means per visit). The plan's `invoicingMethod` is the job
 * type's pricing default and says nothing about a subscription or prepaid term.
 */
export function planBillingMode(
  plan: RecurringBillingPlanSummary,
): 'PER_VISIT' | 'SUBSCRIPTION' | 'PREPAID' {
  const config = plan.scheduleConfig?.recurringConfig;
  const mode =
    config && typeof config === 'object'
      ? (config as { billingMode?: unknown }).billingMode
      : undefined;
  return mode === 'SUBSCRIPTION' || mode === 'PREPAID' ? mode : 'PER_VISIT';
}

function isInvoicingSchedule(value: string | null): value is InvoicingSchedule {
  return (
    value === 'ON_COMPLETION' ||
    value === 'LAST_DAY_OF_MONTH' ||
    value === 'CUSTOM_SCHEDULE' ||
    value === 'AS_NEEDED'
  );
}

/** "Per-visit · on completion", "Subscription · monthly", … — as the job shows it. */
export function planBillingModelLabel(plan: RecurringBillingPlanSummary) {
  return billingModelSummaryFrom({
    billingMode: planBillingMode(plan),
    invoicingSchedule: isInvoicingSchedule(plan.invoicingSchedule) ? plan.invoicingSchedule : null,
  });
}

/**
 * When this engine next runs the plan. It only runs per-visit plans: a
 * subscription period or a prepaid term is billed by its own engine, so their
 * run cursor never moves and read as a run days overdue.
 */
export function planNextRunAt(plan: RecurringBillingPlanSummary) {
  return planBillingMode(plan) === 'PER_VISIT' ? plan.nextAttemptAt : null;
}

export function countByStatus(
  plans: RecurringBillingPlanSummary[],
  status: RecurringBillingPlanStatus,
) {
  return plans.filter((plan) => plan.status === status).length;
}

export function countByRunStatus(
  runs: RecurringBillingRunSummary[],
  status: RecurringBillingRunStatus,
) {
  return runs.filter((run) => run.status === status).length;
}

export function compareValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
) {
  const aValue = typeof a === 'string' ? a.toLowerCase() : (a ?? '');
  const bValue = typeof b === 'string' ? b.toLowerCase() : (b ?? '');
  if (aValue < bValue) return -1;
  if (aValue > bValue) return 1;
  return 0;
}

export function planSearchText(plan: RecurringBillingPlanSummary) {
  return [
    plan.job.jobNumber,
    plan.job.name,
    plan.job.status,
    plan.job.recurringStatus,
    plan.job.bookingType,
    contactLabel(plan),
    plan.contact?.displayName,
    plan.contact?.companyName,
    plan.contact?.primaryEmail,
    storedScheduleLabel(plan),
    plan.status,
    plan.lastErrorCode,
    plan.lastErrorMessage,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function runSearchText(run: RecurringBillingRunSummary) {
  return [
    run.job.jobNumber,
    run.job.name,
    run.job.status,
    contactLabel({ contact: run.contact }),
    run.contact?.displayName,
    run.contact?.companyName,
    run.contact?.primaryEmail,
    run.status,
    run.errorCode,
    run.errorMessage,
    run.invoiceId,
    run.paymentId,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}
